import { APP_MODE } from "entities/App";
import localforage from "localforage";
import isNull from "lodash/isNull";
import loglevel from "loglevel";
import { EComputationCacheName, type ICacheProps } from "./types";
import debounce from "lodash/debounce";

const debugCache = (operation: string, details: any) => {
  console.log(`[AppComputationCache] ${operation}:`, details);
};

interface ICachedData<T> {
  value: T;
  pageId: string;
}

interface ICacheLog {
  lastAccessedAt: number;
  createdAt: number | null;
}

export class AppComputationCache {
  private currentPageId: string | null = null;
  // Singleton instance
  private static instance: AppComputationCache | null = null;
  private static CACHE_KEY_DELIMITER = ">";

  // The cache store for computation results
  private readonly store: LocalForage;

  private readonly inFlightComputation = new Map<string, Promise<unknown>>();

  // The cache store for cache event logs
  private readonly cacheLogsStore: LocalForage;

  private readonly latestCacheKeys = new Map<string, string>();

  // The app mode configuration for each cache type. This determines which app modes
  // the cache should be enabled for
  private readonly appModeConfig = {
    [EComputationCacheName.DEPENDENCY_MAP]: [APP_MODE.PUBLISHED],
    [EComputationCacheName.ALL_KEYS]: [APP_MODE.PUBLISHED],
  };

  setCurrentPageId(pageId: string) {
    this.currentPageId = pageId;
    debugCache('setCurrentPageId', { pageId });
  }

  private validateCacheProps(cacheProps: ICacheProps): boolean {
    if (!cacheProps.pageId) {
      debugCache('validateCacheProps_missingPageId', { cacheProps });
      return false;
    }
    if (this.currentPageId && cacheProps.pageId !== this.currentPageId) {
      debugCache('validateCacheProps_pageIdMismatch', {
        currentPageId: this.currentPageId,
        cachePropsPageId: cacheProps.pageId,
      });
      return false;
    }
    return true;
  }

  constructor() {
    this.store = localforage.createInstance({
      name: "AppComputationCache",
      storeName: "cachedResults",
    });

    this.cacheLogsStore = localforage.createInstance({
      name: "AppComputationCache",
      storeName: "cacheMetadataStore",
    });
  }

  static getInstance(): AppComputationCache {
    if (!AppComputationCache.instance) {
      AppComputationCache.instance = new AppComputationCache();
    }

    return AppComputationCache.instance;
  }

  private debouncedDeleteInvalidCacheEntries = debounce(
    this.deleteInvalidCacheEntries.bind(this),
    200,
  );

  isComputationCacheFeatureEnabled() {
    const isEnabled = false;

    return isEnabled;
  }

  /**
   * Check if the computation result should be cached based on the app mode configuration
   * @returns - A boolean indicating whether the cache should be enabled for the given app mode
   */
  isComputationCached({
    cacheName,
    cacheProps,
  }: {
    cacheName: EComputationCacheName;
    cacheProps: ICacheProps;
  }) {
    const { appMode, timestamp } = cacheProps;

    debugCache('isComputationCached', {
      cacheName,
      appMode,
      timestamp,
      shouldCache: !!(appMode && timestamp && this.appModeConfig[cacheName].includes(appMode))
    });

    if (!appMode || !timestamp || !this.isComputationCacheFeatureEnabled()) {
      return false;
    }

    const shouldCache = this.appModeConfig[cacheName].includes(appMode);
    debugCache('isComputationCached_result', { cacheName, shouldCache });
    return shouldCache;
  }

  /**
   * Checks if the value should be cached based on the app mode configuration and
   * caches the computation result if it should be cached. It also tracks the cache usage
   * @returns - A promise that resolves when the computation result is cached
   */
  async cacheComputationResult<T>({
    cacheName,
    cacheProps,
    computationResult,
  }: {
    cacheProps: ICacheProps;
    cacheName: EComputationCacheName;
    computationResult: T;
  }) {
    const shouldCache = this.isComputationCached({
      cacheName,
      cacheProps,
    });

    debugCache('cacheComputationResult_start', {
      cacheName,
      cacheProps,
      shouldCache,
      computationResultType: typeof computationResult
    });

    if (!shouldCache) {
      debugCache('cacheComputationResult_skipped', { cacheName, reason: 'shouldCache is false' });
      return;
    }

    const effectiveCacheProps = {
      ...cacheProps,
      pageId: cacheProps.pageId ?? this.currentPageId,
    };

    if (!this.validateCacheProps(effectiveCacheProps)) {
      debugCache('cacheComputationResult_invalidProps', {
        cacheName,
        effectiveCacheProps,
      });
      return;
    }
    const cacheKey = this.generateCacheKey({ cacheProps: effectiveCacheProps, cacheName });
    debugCache('cacheComputationResult_key', { cacheKey, cacheName });

    try {
      await this.store.setItem<ICachedData<T>>(cacheKey, {
        value: computationResult,
        pageId: effectiveCacheProps.pageId
      });

      debugCache('cacheComputationResult_stored', {
        cacheKey,
        cacheName,
        valueSize: JSON.stringify(computationResult).length
      });

      await this.trackCacheUsage(cacheKey);
    } catch (error) {
      debugCache('cacheComputationResult_error', {
        cacheKey,
        cacheName,
        error: error instanceof Error ? error.message : error
      });
      loglevel.debug("Error caching computation result:", error);
    }
  }

  /**
   * Gets the cached computation result if it exists and is valid
   * @returns - A promise that resolves with the cached computation result or null if it does not exist
   */
  async getCachedComputationResult<T>({
    cacheName,
    cacheProps,
  }: {
    cacheProps: ICacheProps;
    cacheName: EComputationCacheName;
  }): Promise<T | null> {
    const shouldCache = this.isComputationCached({
      cacheName,
      cacheProps,
    });

    if (!this.validateCacheProps(cacheProps)) {
      debugCache('getCachedComputationResult_invalidProps', {
        cacheName,
        cacheProps,
      });
      return null;
    }

    debugCache('getCachedComputationResult_start', {
      cacheName,
      cacheProps,
      shouldCache
    });

    if (!shouldCache) {
      debugCache('getCachedComputationResult_skipped', { cacheName, reason: 'shouldCache is false' });
      return null;
    }

    const cacheKey = this.generateCacheKey({
      cacheProps,
      cacheName,
    });

    debugCache('getCachedComputationResult_key', { cacheKey, cacheName });

    try {
      const cached = await this.store.getItem<ICachedData<T>>(cacheKey);

      if (isNull(cached)) {
        // Cache miss
        debugCache('getCachedComputationResult_miss', {
          cacheKey,
          cacheName,
          reason: 'Cache miss - no cached data found'
        });
          
        return null;
      }
      debugCache('check cache', { cachedPage: cached.pageId, cacheProps: cacheProps.pageId });
      if (cached.pageId != cacheProps.pageId){
        debugCache('Debounce', { cachedPage: cached.pageId, cacheProps: cacheProps.pageId });
        await this.debouncedDeleteInvalidCacheEntries(cacheProps);
      }

      // Validate that the cache key's pageId matches the requested pageId
      const keyParts = cacheKey.split(AppComputationCache.CACHE_KEY_DELIMITER);
      if (keyParts.length === 6) {
        const [cachedInstanceId, cachedAppId, cachedPageId, cachedAppMode, cachedTimestamp, cachedCacheName] = keyParts;

        if (cachedPageId !== cacheProps.pageId) {
          debugCache('getCachedComputationResult_pageIdMismatch', {
            cacheKey,
            cacheName,
            requestedPageId: cacheProps.pageId,
            cachedPageId,
            error: 'Page ID mismatch - cross-page cache contamination detected'
          });

          // Delete this invalid cache entry
          await this.store.removeItem(cacheKey);
          return null;
        }
      }

      debugCache('getCachedComputationResult_hit', {
        cacheKey,
        cacheName,
        valueSize: JSON.stringify(cached.value).length
      });

      await this.trackCacheUsage(cacheKey);
      await this.debouncedDeleteInvalidCacheEntries(cacheProps);
      return cached.value;
    } catch (error) {
      debugCache('getCachedComputationResult_error', {
        cacheKey,
        cacheName,
        error: error instanceof Error ? error.message : error
      });
      loglevel.error("Error getting cache result:", error);

      return null;
    }
  }

  /**
   * Generates a cache key from the index parts
   * @returns - The generated cache key
   */
  generateCacheKey({
    cacheName,
    cacheProps,
  }: {
    cacheProps: ICacheProps;
    cacheName: EComputationCacheName;
  }) {
    const { appId, appMode, instanceId, pageId, timestamp } = cacheProps;

    // Validate that pageId is not undefined or empty
    if (!pageId) {
      debugCache('generateCacheKey_error', {
        cacheName,
        error: 'pageId is missing or empty',
        cacheProps
      });
      throw new Error(`pageId is required for caching but was: ${pageId}`);
    }

    const timeStampEpoch = new Date(timestamp).getTime();
    const cacheKeyParts = [
      instanceId,
      appId,
      pageId,
      appMode,
      timeStampEpoch,
      cacheName,
    ];

    const cacheKey = cacheKeyParts.join(AppComputationCache.CACHE_KEY_DELIMITER);

    debugCache('generateCacheKey', {
      cacheKey,
      cacheName,
      pageId,
      appId,
      instanceId,
      appMode,
      timeStampEpoch
    });

    return cacheKey;
  }

  /**
   * Fetches the computation result from the cache or computes it if it does not exist
   * @returns - A promise that resolves with the computation result
   * @throws - Logs an error if the computation result cannot be fetched or computed and returns the computed fallback result
   */
  async fetchOrCompute<T>({
    cacheName,
    cacheProps,
    computeFn,
  }: {
    cacheProps: ICacheProps;
    computeFn: () => Promise<T> | T;
    cacheName: EComputationCacheName;
  }) {
    // Capture cacheProps at the start to prevent race conditions
    // when switching pages quickly
    const capturedCacheProps = { ...cacheProps };
    if (!this.validateCacheProps(capturedCacheProps)) {
      debugCache('fetchOrCompute_invalidProps', {
        cacheName,
        cacheProps: capturedCacheProps,
      });
      return computeFn();
    }

    const shouldCache = this.isComputationCached({
      cacheName,
      cacheProps: capturedCacheProps,
    });

    debugCache('fetchOrCompute_start', {
      cacheName,
      cacheProps: capturedCacheProps,
      shouldCache
    });

    if (!shouldCache) {
      debugCache('fetchOrCompute_skipCache', { cacheName, reason: 'shouldCache is false' });
      return computeFn();
    }

    try {

      const appKey = [
        cacheProps.instanceId,
        cacheProps.appId,
        cacheProps.appMode
      ].join(AppComputationCache.CACHE_KEY_DELIMITER);

      const pageLoadKey = [
        cacheProps.instanceId,
        cacheProps.appId,
        cacheProps.pageId,
        cacheProps.appMode,
        new Date(cacheProps.timestamp).getTime(),
      ].join(AppComputationCache.CACHE_KEY_DELIMITER);
      
      this.latestCacheKeys.set(appKey, pageLoadKey);
      
      const cachedResult = await this.getCachedComputationResult<T>({
        cacheProps: capturedCacheProps,
        cacheName,
      });

      if (cachedResult) {
        debugCache('fetchOrCompute_cacheHit', {
          cacheName,
          resultType: typeof cachedResult
        });
        return cachedResult;
      }

      const cacheKey = this.generateCacheKey({
        cacheProps: capturedCacheProps,
        cacheName
      })

      if (this.inFlightComputation.has(cacheKey)) {
        return this.inFlightComputation.get(cacheKey) as Promise<T>;
      }


      debugCache('fetchOrCompute_cacheMiss', { cacheName, reason: 'No cached result found' });
      const computationPromise = (async () => {
        const computationResult = await computeFn();
        
        
        if (this.latestCacheKeys.get(appKey) !== pageLoadKey) {
          debugCache('Discarding stale result ', {
            cacheName,
            cacheKey,
            pageLoadKey,
          });
          return computationResult;
        }

        await this.cacheComputationResult({
          cacheProps: capturedCacheProps,
          computationResult,
          cacheName,
        });
        return computationResult;
      })();

      this.inFlightComputation.set(cacheKey, computationPromise);
      computationPromise.finally(() => {
        this.inFlightComputation.delete(cacheKey);
      })
      return computationPromise;
    } catch (error) {
      debugCache('fetchOrCompute_error', {
        cacheName,
        error: error instanceof Error ? error.message : error
      });
      loglevel.error("Error getting cache result:", error);
      const fallbackResult = await computeFn();
      debugCache('fetchOrCompute_fallback', {
        cacheName,
        fallbackResultType: typeof fallbackResult
      });

      return fallbackResult;
    }
  }

  /**
   * Tracks the cache usage by updating the last accessed timestamp of the cache
   * @param name - The name of the cache
   * @returns - A promise that resolves when the cache usage is tracked
   * @throws - Logs an error if the cache usage cannot be tracked
   */
  async trackCacheUsage(name: string) {
    try {
      const currentLog = await this.cacheLogsStore.getItem<ICacheLog>(name);
      const newLog = {
        lastAccessedAt: Date.now(),
        createdAt: currentLog?.createdAt || Date.now(),
      };

      debugCache('trackCacheUsage', {
        cacheKey: name,
        currentLog,
        newLog
      });

      await this.cacheLogsStore.setItem(name, newLog);
    } catch (error) {
      debugCache('trackCacheUsage_error', {
        cacheKey: name,
        error: error instanceof Error ? error.message : error
      });
      loglevel.error("Error tracking cache usage:", error);
    }
  }

  /**
   * Delete invalid cache entries
   * @returns - A promise that resolves when the invalid cache entries are deleted
   */

  async deleteInvalidCacheEntries(cacheProps: ICacheProps) {
    try {
      debugCache('deleteInvalidCacheEntries_start', { cacheProps });

      // Get previous entry keys
      const cacheKeys = await this.store.keys();
      debugCache('deleteInvalidCacheEntries_foundKeys', { totalKeys: cacheKeys.length });

      // Get invalid cache keys
      const invalidCacheKeys = cacheKeys.filter((key) => {
        const keyParts = key.split(AppComputationCache.CACHE_KEY_DELIMITER);

        if (keyParts.length !== 6) {
          debugCache('deleteInvalidCacheEntries_invalidKeyFormat', { key, keyParts });
          return false;
        }

        const [instanceId, appId, pageId, appMode, cacheKeyTimestampString, cacheName] = keyParts
        const cacheKeyTimestamp = parseInt(cacheKeyTimestampString, 10);
        if (!cacheProps.timestamp) {
          return false;
        }
        const currentTimeStamp = new Date(cacheProps.timestamp).getTime();

        const isInvalid = (
          instanceId === cacheProps.instanceId &&
          appId === cacheProps.appId &&
          pageId === cacheProps.pageId &&
          appMode === cacheProps.appMode &&
          cacheKeyTimestamp !== currentTimeStamp &&
          !isNaN(cacheKeyTimestamp)
        );

        if (isInvalid) {
          debugCache('deleteInvalidCacheEntries_invalidKey', {
            key,
            instanceId,
            appId,
            pageId,
            appMode,
            cacheKeyTimestamp,
            currentTimeStamp
          });
        }

        return isInvalid;
      });

      debugCache('deleteInvalidCacheEntries_invalidKeys', {
        count: invalidCacheKeys.length,
        keys: invalidCacheKeys
      });

      if (invalidCacheKeys.length === 0) {
        debugCache('deleteInvalidCacheEntries_noInvalidKeys', {});
        return;
      }

      // Delete invalid cache entries
      await Promise.all(
        invalidCacheKeys.map(async (key) => {
          debugCache('deleteInvalidCacheEntries_removing', { key });
          return this.store.removeItem(key);
        }),
      );

      // Delete invalid cache logs
      await Promise.all(
        invalidCacheKeys.map(async (key) => {
          debugCache('deleteInvalidCacheEntries_removingLog', { key });
          return this.cacheLogsStore.removeItem(key);
        }),
      );

      debugCache('deleteInvalidCacheEntries_completed', {
        deletedKeys: invalidCacheKeys.length
      });
    } catch (error) {
      debugCache('deleteInvalidCacheEntries_error', {
        cacheProps,
        error: error instanceof Error ? error.message : error
      });
      loglevel.error("Error deleting invalid cache entries:", error);
    }
  }

  static resetInstance() {
    AppComputationCache.instance = null;
  }
}

export const appComputationCache = AppComputationCache.getInstance();

export default appComputationCache;
